import { create } from 'zustand'

enum FeatureFlag {
  BOT_THEME = 'botTheme',
  BUBBLE = 'bubble',
  CAMPAIGN = 'campaign',
  CAMPAIGN_DISPATCHER_ENGINE_V2_VERSION = 'campaignDispatcherEngineV2Version',
  CONVERSATION_HISTORY = 'conversationHistory',
  FINN = 'finn',
  FOLLOW_UP = 'followUp',
  INTEGRATIONS = 'integrations',
  N8N = 'n8n',
}

type FeatureFlagValues = Record<FeatureFlag, boolean>

type FeatureFlagApiKey = keyof typeof FeatureFlag

const defaultFeatureFlags: FeatureFlagValues = {
  [FeatureFlag.BOT_THEME]: false,
  [FeatureFlag.BUBBLE]: false,
  [FeatureFlag.CAMPAIGN]: false,
  [FeatureFlag.CAMPAIGN_DISPATCHER_ENGINE_V2_VERSION]: false,
  [FeatureFlag.CONVERSATION_HISTORY]: false,
  [FeatureFlag.FINN]: false,
  [FeatureFlag.FOLLOW_UP]: false,
  [FeatureFlag.INTEGRATIONS]: false,
  [FeatureFlag.N8N]: false,
}

type FeatureFlagsStore = {
  featureFlags: FeatureFlagValues
  isLoaded: boolean
  setFeatureFlag: (featureFlag: FeatureFlag, value: boolean) => void
  setFeatureFlags: (flags: Partial<Record<FeatureFlagApiKey, boolean>>) => void
  markAsLoaded: () => void
  resetFeatureFlags: () => void
}

const useFeatureFlags = create<FeatureFlagsStore>()((set) => ({
  featureFlags: defaultFeatureFlags,
  isLoaded: false,
  setFeatureFlag: (featureFlag, value) =>
    set((state) => ({
      featureFlags: {
        ...state.featureFlags,
        [featureFlag]: value,
      },
    })),
  setFeatureFlags: (flags) =>
    set(() => {
      const next: FeatureFlagValues = { ...defaultFeatureFlags }

      for (const [apiKey, value] of Object.entries(flags)) {
        const storeKey = FeatureFlag[apiKey as FeatureFlagApiKey]
        if (storeKey && typeof value === 'boolean') {
          next[storeKey] = value
        }
      }

      if (import.meta.env.DEV) {
        for (const key of Object.values(FeatureFlag)) {
          if (localStorage.getItem(`ff_${key}`) === 'true') {
            next[key] = true
          }
        }
      }

      return { featureFlags: next, isLoaded: true }
    }),
  markAsLoaded: () => set({ isLoaded: true }),
  resetFeatureFlags: () =>
    set({ featureFlags: defaultFeatureFlags, isLoaded: false }),
}))

export { FeatureFlag, useFeatureFlags }
